import { useState, useEffect, useCallback } from 'react';
import ReactDOM from 'react-dom/client';
import mapboxgl, { mapStyles } from 'utils/mapbox-gl';
import { useRef } from 'react';

import { transformPorts } from './helpers';
import Port from './Port';
import variables from 'common/assets/scss/abstracts/_exports.module.scss';
import Vessel from './Vessel';
import mapImg from 'assets/img/world-map-map-view.png';
import satelliteImg from 'assets/img/world-map-satellite-view.png';
import { Button } from 'reactstrap';
import PropTypes from 'prop-types';

const Map = ({
  ports,
  showZoom = true,
  canMaximize = true,
  maxZoom = 6,
  route,
  zoomToRoute,
  id = '',
  vesselPosition,
  clearMarkersOnVesselPositionChange = false,
  onViewChange,
  dottedRoute,
  defaultView,
  showTooltip,
  dottedRoutes = [], //Use with lineRoutes to render both dotted and line routes
  lineRoutes = [] // Else just use dottedRoute prop
}) => {
  const center = [-13.217886, 54.100689];
  const zoom = 1.5;
  const markers = useRef([]);
  const [mapLoaded, setMapLoaded] = useState(false);
  const [map, setMap] = useState(null);
  const [currentView, setCurrentView] = useState(defaultView ? defaultView : 'satellite');

  const clearMarkers = useCallback(() => {
    if (markers?.current?.length) {
      markers?.current?.forEach(marker => {
        if (marker) marker.remove();
      });
    }
  }, [markers?.current]);

  useEffect(() => {
    if (mapLoaded && onViewChange) {
      onViewChange(currentView);
    }
  }, [currentView]);

  //Initialize map
  useEffect(() => {
    let newMap = new mapboxgl.Map({
      container: `mapbox-general-map-${id}`,
      style: mapStyles[currentView],
      center,
      zoom,
      maxZoom: 15
    });

    if (canMaximize) {
      newMap.addControl(new mapboxgl.FullscreenControl());
    }

    if (showZoom) {
      newMap.addControl(new mapboxgl.NavigationControl());
    }

    newMap.scrollZoom.setWheelZoomRate(1 / 200);

    newMap.on('load', function () {
      newMap.resize();
      newMap.setZoom(1.1);
      setMap(newMap);
      setMapLoaded(true);
    });

    return () => {
      if (map) {
        map.remove();
        setMap(null);
      }
    };
  }, []);

  //Render Ports

  const renderPorts = useCallback(
    view => {
      if (!ports || !Array.isArray(ports) || ports.length === 0) return;

      let newMarkers = [];

      const transformedPorts = transformPorts(ports, view);

      transformedPorts.forEach((port, i) => {
        if (port.icons) {
          const markerElement = document.createElement('div');
          markerElement.className = `port-action-${i}`;

          const root = ReactDOM.createRoot(markerElement);
          root.render(<Port port={port} view={view} icon={'bullet'} />);

          const popUpElement = document.createElement('div');

          const popUpRoot = ReactDOM.createRoot(popUpElement);
          popUpRoot.render(
            <div>
              {port?.port_name} - {port?.port_code} - {port?.port_id}
            </div>
          );

          let newMarker = showTooltip
            ? new mapboxgl.Marker(markerElement)
                .setLngLat(port.coords)
                .addTo(map)
                .setPopup(
                  new mapboxgl.Popup({
                    offset: 15,
                    closeOnClick: true,
                    closeButton: false,
                    className: `port-tooltip-satellite--${view === 'satellite' ? 'light' : 'dark'}`
                  })
                    .setDOMContent(popUpElement)
                    .setMaxWidth('388px')
                )
            : new mapboxgl.Marker(markerElement).setLngLat(port.coords).addTo(map);
          newMarkers.push(newMarker);
        }
      });

      markers.current = newMarkers;

      if (!zoomToRoute || (!route && transformedPorts.length > 0)) {
        const bounds = transformedPorts.reduce(function (bounds, port) {
          return bounds.extend(port.coords);
        }, new mapboxgl.LngLatBounds(transformedPorts[0].coords, transformedPorts[0].coords));

        map.fitBounds(bounds, {
          padding: 50,
          maxZoom
        });
      }
    },
    [map, maxZoom, route, ports, showTooltip, zoomToRoute]
  );

  useEffect(() => {
    clearMarkers();

    if (!mapLoaded || !ports || (Array.isArray(ports) && ports.length === 0)) return;

    renderPorts(currentView);
  }, [ports, mapLoaded, currentView]);

  const removePrevLayers = () => {
    if (!map) return;
    //remove existing layers with the same id
    const existingSource = map.getSource(`route-${id}`);
    const existingLayer = map.getLayer(`route-${id}`);

    if (existingLayer) map.removeLayer(`route-${id}`);
    if (existingSource) map.removeSource(`route-${id}`);
  };

  //Render routes
  const renderRoutes = view => {
    map.addSource(`route-${id}`, {
      type: 'geojson',
      data: {
        type: 'FeatureCollection',
        features: [
          {
            type: 'Feature',
            properties: {},
            geometry: {
              type: 'MultiLineString',
              coordinates: route
            }
          }
        ]
      }
    });

    let paint = {
      'line-color': view === 'satellite' ? variables.red : variables.primary,
      'line-width': 1
    };

    if (dottedRoute) {
      paint['line-dasharray'] = [0, 3];
      paint['line-width'] = 2;
    }

    map.addLayer({
      id: `route-${id}`,
      type: 'line',
      source: `route-${id}`,
      layout: {
        'line-cap': 'round'
      },
      paint: paint
    });

    if (zoomToRoute) {
      let allBounds = [];
      route.forEach(r => {
        allBounds = [...allBounds, ...r];
      });

      const bounds = allBounds.reduce(function (bounds, coord) {
        return bounds.extend(coord);
      }, new mapboxgl.LngLatBounds(allBounds[0], allBounds[0]));

      map.fitBounds(bounds, {
        padding: 50,
        maxZoom: 6
      });
    }
  };

  useEffect(() => {
    removePrevLayers();
    if (!mapLoaded || !route || (Array.isArray(route) && route.length === 0)) return;
    renderRoutes(currentView);
  }, [route, mapLoaded]);

  //Render dotted and lined routes if applicable
  const renderExtraRoutes = view => {
    //remove existing layers with the same id
    const existingSource1 = map.getSource(`route-dotted-${id}`);
    const existingLayer1 = map.getLayer(`route-dotted-${id}`);

    const existingSource2 = map.getSource(`route-line-${id}`);
    const existingLayer2 = map.getLayer(`route-line-${id}`);

    if (existingLayer1) map.removeLayer(`route-dotted-${id}`);
    if (existingSource1) map.removeSource(`route-dotted-${id}`);
    if (existingLayer2) map.removeLayer(`route-line-${id}`);
    if (existingSource2) map.removeSource(`route-line-${id}`);

    map.addSource(`route-dotted-${id}`, {
      type: 'geojson',
      data: {
        type: 'FeatureCollection',
        features: [
          {
            type: 'Feature',
            properties: {},
            geometry: {
              type: 'MultiLineString',
              coordinates: dottedRoutes
            }
          }
        ]
      }
    });

    map.addSource(`route-line-${id}`, {
      type: 'geojson',
      data: {
        type: 'FeatureCollection',
        features: [
          {
            type: 'Feature',
            properties: {},
            geometry: {
              type: 'MultiLineString',
              coordinates: lineRoutes
            }
          }
        ]
      }
    });

    let paintLine = {
      'line-color': view === 'satellite' ? variables.red : variables.primary,
      'line-width': 1
    };

    let paintDot = {
      'line-color': view === 'satellite' ? variables.red : variables.primary,
      'line-width': 1,
      'line-dasharray': [0, 3],
      'line-width': 2
    };

    map.addLayer({
      id: `route-dotted-${id}`,
      type: 'line',
      source: `route-dotted-${id}`,
      layout: {
        'line-cap': 'round'
      },
      paint: paintDot
    });

    map.addLayer({
      id: `route-line-${id}`,
      type: 'line',
      source: `route-line-${id}`,
      layout: {
        'line-cap': 'round'
      },
      paint: paintLine
    });

    if (zoomToRoute) {
      let allBounds = [];
      [...lineRoutes, ...dottedRoutes].forEach(r => {
        allBounds = [...allBounds, ...r];
      });

      const bounds = allBounds.reduce(function (bounds, coord) {
        return bounds.extend(coord);
      }, new mapboxgl.LngLatBounds(allBounds[0], allBounds[0]));

      map.fitBounds(bounds, {
        padding: 50,
        maxZoom: 6
      });
    }
  };

  useEffect(() => {
    if (
      !mapLoaded ||
      (!lineRoutes && !dottedRoutes) ||
      (Array.isArray(lineRoutes) &&
        lineRoutes.length === 0 &&
        Array.isArray(dottedRoutes) &&
        dottedRoutes.length === 0)
    )
      return;
    renderExtraRoutes(currentView);
  }, [lineRoutes, dottedRoutes, mapLoaded]);

  //Render Vessel

  const renderVessel = view => {
    if (clearMarkersOnVesselPositionChange) {
      clearMarkers();
    }
    let coords = [vesselPosition.lng, vesselPosition.lat];

    const markerElement = document.createElement('div');
    markerElement.className = `vessel-action`;

    const root = ReactDOM.createRoot(markerElement);
    root.render(<Vessel view={view} />);

    let newMarker = new mapboxgl.Marker(markerElement).setLngLat(coords).addTo(map);
    newMarker.setRotation(vesselPosition.rotation);

    markers.current = [...markers.current, newMarker];
  };

  useEffect(() => {
    if (!mapLoaded) return;
    if (!vesselPosition) {
      clearMarkers();
      return;
    }
    renderVessel(currentView);
  }, [vesselPosition]);

  const paintLayers = view => {
    if (route && Array.isArray(route) && route.length > 0) renderRoutes(view);
    if (ports && Array.isArray(ports) && ports.length > 0) renderPorts(view);
    if (vesselPosition) renderVessel(view);
    if (
      (lineRoutes || dottedRoutes) &&
      ((Array.isArray(lineRoutes) && lineRoutes.length !== 0) ||
        (Array.isArray(dottedRoutes) && dottedRoutes.length !== 0))
    ) {
      renderExtraRoutes(view);
    }
  };

  const onSatelliteClick = () => {
    if (currentView !== 'satellite') {
      setCurrentView('satellite');
      map.setStyle(mapStyles.satellite);
      setTimeout(() => {
        paintLayers('satellite');
      }, 350);
    }
  };

  const onSimpleClick = () => {
    if (currentView !== 'simple') {
      setCurrentView('simple');
      map.setStyle(mapStyles.simple);
      setTimeout(() => {
        paintLayers('simple');
      }, 350);
    }
  };

  return (
    <div className="general-map h-100p" id={`mapbox-general-map-${id}`}>
      <div className="general-map__buttons-container">
        {currentView === 'simple' ? (
          <Button
            color="link"
            className={`p-0  text-start single-button ${
              currentView === 'satellite' ? 'active' : ''
            }`}
            type="button"
            onClick={onSatelliteClick}
          >
            <img alt="satellite view" src={satelliteImg} width={48} height={48} />
          </Button>
        ) : (
          <Button
            color="link"
            className={`p-0 text-start single-button ${currentView === 'simple' ? 'active' : ''}`}
            type="button"
            onClick={onSimpleClick}
          >
            <img alt="simple map view" src={mapImg} width={48} height={48} />
          </Button>
        )}
      </div>
    </div>
  );
};

Map.propTypes = {
  ports: PropTypes.array, //Ports to render with their actions
  showZoom: PropTypes.bool, //if user can zoom map. Default is true
  canMaximize: PropTypes.bool, // If user can maximize map. Default  is true
  route: PropTypes.array, // array of points e.g [[3.44544,2.44333],[4.565655, 9.4545454]] -- [lng,lat]
  zoomToRoute: PropTypes.bool, // If true the rendered view will be the boundaries of the map
  id: PropTypes.string, // Unique id if needed to render many maps at the same time
  vesselPosition: PropTypes.object, // Vessel info. Object that has keys lng, lat
  dottedRoute: PropTypes.bool, // If true the routes will be dotted else straight line
  onViewChange: PropTypes.func, // Returns the current view. One of satellite or simple
  showTooltip: PropTypes.bool
};

export default Map;
